import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Building, 
  Users, 
  CreditCard, 
  Settings, 
  Calendar, 
  Clock,
  AlertTriangle,
  CheckCircle,
  Coins,
  Activity,
  FileText,
  Shield
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';

interface Workspace {
  id: string;
  name: string;
  slug?: string;
  status?: string;
  plan?: { 
    id: string;
    name: string;
    code: string;
  };
  organization?: {
    id: string;
    name: string;
  };
  created_at: string;
  updated_at?: string;
  members?: any[];
  trial?: {
    status: string;
    ends_at: string;
  };
}

interface WorkspaceViewModalProps {
  workspace: Workspace | null;
  isOpen: boolean;
  onClose: () => void;
}

export function WorkspaceViewModal({ workspace, isOpen, onClose }: WorkspaceViewModalProps) {
  // Fetch detailed workspace data
  const { data: workspaceDetails, isLoading } = useQuery({
    queryKey: ['workspace-details', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return null;
      
      const { data, error } = await supabase.functions.invoke('workspaces-manage', {
        body: {
          action: 'get_workspace',
          workspace_id: workspace.id
        }
      });
      
      if (error) throw error;
      return data.workspace;
    },
    enabled: !!workspace?.id && isOpen
  });

  // Fetch workspace activity logs
  const { data: activityLogs = [] } = useQuery({
    queryKey: ['workspace-activity', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('resource_type', 'workspace')
        .eq('resource_id', workspace.id)
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) {
        console.log('Activity logs not available:', error);
        return [];
      }
      return data || [];
    },
    enabled: !!workspace?.id && isOpen
  });

  // Fetch workspace billing data
  const { data: billingData } = useQuery({
    queryKey: ['workspace-billing', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return null;
      
      const { data, error } = await supabase
        .from('workspace_credit_wallets')
        .select('*')
        .eq('workspace_id', workspace.id)
        .single();
      
      if (error) {
        console.log('Billing data not available:', error);
        return null;
      }
      return data;
    },
    enabled: !!workspace?.id && isOpen
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'trial': return 'bg-yellow-100 text-yellow-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPlanColor = (planCode: string) => {
    switch (planCode) {
      case 'enterprise': return 'bg-purple-100 text-purple-800';
      case 'customer': return 'bg-blue-100 text-blue-800';
      case 'trial': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getHealthStatus = () => {
    if (!workspaceDetails) return { status: 'unknown', color: 'bg-gray-100 text-gray-800' };
    
    const issues = [];
    
    if (workspaceDetails.trial?.status === 'expired') {
      issues.push('Trial expired');
    }
    
    if (billingData?.credits_balance && billingData.credits_balance < 100) {
      issues.push('Low credits');
    }
    
    if (workspaceDetails.status === 'suspended') {
      issues.push('Suspended');
    }
    
    if (issues.length === 0) {
      return { status: 'healthy', color: 'bg-green-100 text-green-800', icon: CheckCircle };
    } else if (issues.some(i => i.includes('expired') || i.includes('suspended'))) {
      return { status: 'critical', color: 'bg-red-100 text-red-800', icon: AlertTriangle };
    } else {
      return { status: 'warning', color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle };
    }
  };

  if (!workspace) return null;

  const details = workspaceDetails || workspace;
  const health = getHealthStatus();
  const HealthIcon = health.icon || CheckCircle;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Building className="h-5 w-5" />
            <span>{workspace.name}</span>
          </DialogTitle>
          <DialogDescription>
            Comprehensive workspace overview and details
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Status Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center space-x-2">
                    <Activity className="h-4 w-4" />
                    <span>Health Status</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-2">
                    <HealthIcon className="h-4 w-4" />
                    <Badge className={health.color}>
                      {health.status}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center space-x-2">
                    <Settings className="h-4 w-4" />
                    <span>Status</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Badge className={getStatusColor(details.status || 'active')}>
                    {details.status || 'active'}
                  </Badge>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center space-x-2">
                    <CreditCard className="h-4 w-4" />
                    <span>Plan</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Badge className={getPlanColor(details.plan?.code || 'trial')}>
                    {details.plan?.name || 'Trial'}
                  </Badge>
                </CardContent>
              </Card>
            </div>

            {/* Workspace Details */}
            <Card>
              <CardHeader>
                <CardTitle>Workspace Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Name</label>
                      <p className="font-medium">{details.name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Slug</label>
                      <p className="font-mono text-sm">{details.slug || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Organization</label>
                      <p>{details.organization?.name || '-'}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Created</label>
                      <p>{new Date(details.created_at).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
                      <p>
                        {details.updated_at 
                          ? formatDistanceToNow(new Date(details.updated_at), { addSuffix: true })
                          : '-'
                        }
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Members</label>
                      <p>{details.members?.length || 0} users</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trial Information */}
            {details.trial && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Clock className="h-4 w-4" />
                    <span>Trial Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Status</label>
                      <p>
                        <Badge className={details.trial.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {details.trial.status}
                        </Badge>
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Ends</label>
                      <p>{new Date(details.trial.ends_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <span>Workspace Members</span>
                </CardTitle>
                <CardDescription>
                  Users with access to this workspace
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-2">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="animate-pulse">
                        <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-muted rounded w-1/2"></div>
                      </div>
                    ))}
                  </div>
                ) : details.members && details.members.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Joined</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {details.members.map((member: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{member.email || `user-${index}@example.com`}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{member.role || 'member'}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-green-100 text-green-800">
                              {member.status || 'active'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {member.joined_at 
                              ? new Date(member.joined_at).toLocaleDateString()
                              : 'Unknown'
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    No members found
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Coins className="h-4 w-4" />
                    <span>Credit Balance</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {billingData?.credits_balance?.toFixed(0) || '0'} credits
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Current balance
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <CreditCard className="h-4 w-4" />
                    <span>Billing Status</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Badge className="bg-green-100 text-green-800">
                    Active
                  </Badge>
                  <p className="text-sm text-muted-foreground mt-2">
                    No payment issues
                  </p>
                </CardContent>
              </Card>
            </div>

            {billingData && (
              <Card>
                <CardHeader>
                  <CardTitle>Billing Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Auto Top-up</label>
                      <p>{billingData.auto_topup_enabled ? 'Enabled' : 'Disabled'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Low Balance Threshold</label>
                      <p>{billingData.low_balance_threshold || 100} credits</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Critical Balance Threshold</label>
                      <p>{billingData.critical_balance_threshold || 50} credits</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
                      <p>
                        {billingData.updated_at 
                          ? formatDistanceToNow(new Date(billingData.updated_at), { addSuffix: true })
                          : 'Never'
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="h-4 w-4" />
                  <span>Recent Activity</span>
                </CardTitle>
                <CardDescription>
                  Latest workspace changes and events
                </CardDescription>
              </CardHeader>
              <CardContent>
                {activityLogs.length > 0 ? (
                  <div className="space-y-3">
                    {activityLogs.map((log: any, index: number) => (
                      <div key={index} className="flex items-start space-x-3 pb-3 border-b last:border-b-0">
                        <Shield className="h-4 w-4 mt-1 text-muted-foreground" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{log.action}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    No recent activity
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end pt-4">
          <Button onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}